import {useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {AddButtonPropsSingular} from 'shells/card/card-widget/card-widget';

import type {
    NutFileInput,
    WidgetFragment as Widget,
    GetAttachmentsForLead_lead_files as File,
} from 'nutshell-graphql-types';

import {FilestackFilePicker} from '../../../../../filestack-file-picker';
import type {File as FilestackFile} from '../../../../../filestack-file-picker';
import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../../core/card/helpers';

import {AttachmentRow} from './attachment-row';

type Props = {
    user: User | null | undefined;
    entityId: string;
    widget: Widget;
    files: File[] | null | undefined;
    isLoadingAttachments: boolean;
    onRemoveAttachment: (fileId: string) => Promise<unknown>;
    onAddAttachment: (filesToAdd: NutFileInput[]) => Promise<unknown>;
};

export function AttachmentsWidget(props: Props) {
    const {files, isLoadingAttachments, onRemoveAttachment, onAddAttachment} = props;

    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [error, setError] = useState<string | null | undefined>(null);

    const handleRemoveAttachment = (fileId: string) => {
        onRemoveAttachment(fileId).catch((e) => {
            setError(safelyGetGraphQLErrorMessage(e));
        });
    };

    const handleAddAttachment = (filesToAdd: FilestackFile[]) => {
        const filesToAddMapped = filesToAdd.map((file) => ({
            filename: file.filename,
            filesize: file.size,
            locationUri: file.url,
            mimeType: file.mimetype,
        }));

        onAddAttachment(filesToAddMapped).catch((e) => {
            setError(safelyGetGraphQLErrorMessage(e));
        });
    };

    const addButtonProps: AddButtonPropsSingular = {
        label: 'Upload',
        onClick: () => {
            setIsUploading(true);
        },
        variant: 'singular',
        isDisabled: isUploading,
    };

    const count = files ? files.length : 0;
    const isEmpty = count === 0;

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                addButtonProps={addButtonProps}
                errorProps={{error, setError}}
                emptyStateProps={getWidgetEmptyStateProps('leadAttachments', isEmpty)}
                isLoading={isLoadingAttachments}
                count={count}
            >
                {files &&
                    files.map((file) => (
                        <AttachmentRow
                            key={file.fileId}
                            fileId={file.fileId}
                            name={file.name}
                            fileType={file.fileType}
                            href={file.href}
                            viewHref={file.viewHref}
                            viewableInBrowser={file.viewableInBrowser}
                            uploadedTime={file.uploadedTime}
                            userName={file.creatorUser && file.creatorUser.name}
                            userAvatarUrl={file.creatorUser && file.creatorUser.avatarUrl}
                            userInitials={file.creatorUser && file.creatorUser.initials}
                            onRemove={handleRemoveAttachment}
                        />
                    ))}
            </CollapsibleCardWidget>
            <FilestackFilePicker
                isMultiSelect={true}
                onSuccess={({filesUploaded}) => {
                    setIsUploading(false);
                    handleAddAttachment(filesUploaded);
                }}
                isOpen={isUploading}
                onClose={() => {
                    setIsUploading(false);
                }}
            />
        </>
    );
}
